import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import EscarapelaIcon from '../components/ui/EscarapelaIcon';
import SolDivider from '../components/ui/SolDivider';
import NegocioFormModal from '../components/ui/NegocioFormModal';
import {
  negocioTipoToUi,
  negocioStatusToUi,
  negocioFotoPublicUrl,
} from '../lib/negocioHelpers';
import {
  avatarPublicUrl,
  uploadMemberAvatar,
  deleteMemberAvatar,
  validateWhatsappNumber,
  normalizeOptionalUrl,
  BIO_MAX_LEN,
} from '../lib/memberHelpers';
import { BADGES_INFO } from '../mockData';
import { motion, AnimatePresence } from 'framer-motion';

const STATUS_BADGE_CLASS = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  flagged: 'bg-orange-50 text-orange-700 border-orange-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
};

const emptyFormFromUser = (user) => ({
  nombre: user?.nombre || '',
  rubro: user?.rubro || '',
  capitulo_id: user?.capitulo_id || '',
  whatsapp_number: user?.whatsapp_number || '',
  whatsapp_visible: !!user?.whatsapp_visible,
  bio: user?.bio || '',
  ofrezco: user?.ofrezco || '',
  busco: user?.busco || '',
  instagram_url: user?.instagram_url || '',
  linkedin_url: user?.linkedin_url || '',
  web_url: user?.web_url || '',
});

export default function MyProfile() {
  const {
    currentUser,
    logout,
    updateFeaturedBadges,
    updateMyProfile,
    negociosServicios,
    createNegocioServicio,
    updateNegocioServicio,
    deleteNegocioServicio,
    CAPITULOS,
    RUBROS,
  } = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => emptyFormFromUser(null));
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [negocioModalOpen, setNegocioModalOpen] = useState(false);
  const [editingNegocio, setEditingNegocio] = useState(null);
  const [negocioError, setNegocioError] = useState('');
  const [negocioSuccess, setNegocioSuccess] = useState('');
  const fileInputRef = useRef(null);

  const myNegocios = (negociosServicios || []).filter((n) => n.member_id === currentUser?.id);
  const capituloNombre =
    (CAPITULOS || []).find((c) => c.id === currentUser?.capitulo_id)?.nombre || null;

  useEffect(() => {
    if (!currentUser || isEditing) return;
    setForm(emptyFormFromUser(currentUser));
  }, [currentUser, isEditing]);

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  if (!currentUser) return null;

  const userBadges = currentUser.badges || [];
  const featuredCount = userBadges.filter((b) => b.featured).length;
  const displayFoto =
    avatarPreview ||
    avatarPublicUrl(currentUser.foto_url) ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(currentUser.nombre || '?')}`;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const resetAvatarDraft = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleToggleFeatured = async (badgeId) => {
    if (badgeId === 'abanderado') return;

    const currentBadge = userBadges.find((b) => b.id === badgeId);
    const isCurrentlyFeatured = currentBadge?.featured;

    if (!isCurrentlyFeatured && featuredCount >= 3) {
      return;
    }

    const newBadges = userBadges.map((b) =>
      b.id === badgeId ? { ...b, featured: !b.featured } : b
    );

    try {
      await updateFeaturedBadges(currentUser.id, newBadges);
    } catch (err) {
      console.error('No se pudo actualizar insignias destacadas:', err);
    }
  };

  const handleCancelEdit = () => {
    setForm(emptyFormFromUser(currentUser));
    resetAvatarDraft();
    setSaveError('');
    setPhotoError('');
    setIsEditing(false);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setPhotoError('Elegí una imagen (JPG, PNG o WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('La imagen no puede superar 5 MB');
      return;
    }
    setPhotoError('');
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    setSaveError('');
    setPhotoError('');

    const nombre = form.nombre.trim();
    if (!nombre) {
      setSaveError('El nombre es obligatorio');
      return;
    }

    const bio = form.bio || '';
    if (bio.length > BIO_MAX_LEN) {
      setSaveError(`La bio no puede superar ${BIO_MAX_LEN} caracteres`);
      return;
    }

    const wa = validateWhatsappNumber(form.whatsapp_number);
    if (!wa.ok) {
      setSaveError(wa.error);
      return;
    }

    const ig = normalizeOptionalUrl(form.instagram_url);
    const li = normalizeOptionalUrl(form.linkedin_url);
    const web = normalizeOptionalUrl(form.web_url);
    if (!ig.ok) {
      setSaveError(`Instagram: ${ig.error}`);
      return;
    }
    if (!li.ok) {
      setSaveError(`LinkedIn: ${li.error}`);
      return;
    }
    if (!web.ok) {
      setSaveError(`Web: ${web.error}`);
      return;
    }

    setIsSaving(true);

    let fotoPath = currentUser.foto_url || null;
    let uploadedNewPath = null;
    let localPhotoError = '';

    if (avatarFile) {
      try {
        uploadedNewPath = await uploadMemberAvatar(currentUser.id, avatarFile);
        fotoPath = uploadedNewPath;
      } catch (err) {
        console.error('Avatar upload failed:', err);
        localPhotoError = err.message || 'No se pudo subir la foto de perfil';
        // Keep previous foto_url; still save the rest of the form
        fotoPath = currentUser.foto_url || null;
        uploadedNewPath = null;
      }
    }

    try {
      await updateMyProfile({
        nombre,
        rubro: form.rubro,
        capitulo_id: form.capitulo_id || null,
        whatsapp_number: wa.value,
        whatsapp_visible: form.whatsapp_visible,
        bio: bio.trim(),
        ofrezco: form.ofrezco,
        busco: form.busco,
        instagram_url: ig.value,
        linkedin_url: li.value,
        web_url: web.value,
        foto_url: fotoPath,
      });

      if (uploadedNewPath && currentUser.foto_url && currentUser.foto_url !== uploadedNewPath) {
        try {
          await deleteMemberAvatar(currentUser.foto_url);
        } catch (err) {
          console.warn('No se pudo borrar el avatar anterior:', err);
        }
      }

      resetAvatarDraft();
      setIsEditing(false);
      if (localPhotoError) {
        setPhotoError(localPhotoError);
      } else {
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('No se pudo guardar el perfil:', err);
      // If DB update failed after a successful upload, remove the orphan new file
      if (uploadedNewPath) {
        try {
          await deleteMemberAvatar(uploadedNewPath);
        } catch (cleanupErr) {
          console.warn('Cleanup avatar after failed save:', cleanupErr);
        }
      }
      setSaveError(err.message || 'No se pudo guardar el perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const openCreateNegocio = () => {
    setEditingNegocio(null);
    setNegocioError('');
    setNegocioModalOpen(true);
  };

  const openEditNegocio = (listing) => {
    setEditingNegocio(listing);
    setNegocioError('');
    setNegocioModalOpen(true);
  };

  const handleNegocioSubmit = async (payload) => {
    if (payload.listingId) {
      await updateNegocioServicio(payload);
      setNegocioSuccess('Listing actualizado');
    } else {
      await createNegocioServicio(payload);
      setNegocioSuccess('Enviado a revisión');
    }
    setTimeout(() => setNegocioSuccess(''), 3000);
  };

  const handleDeleteNegocio = async (listing) => {
    if (!window.confirm(`¿Eliminar “${listing.nombre}”? Esta acción no se puede deshacer.`)) return;
    setNegocioError('');
    try {
      await deleteNegocioServicio(listing.id);
    } catch (err) {
      console.error(err);
      setNegocioError(err.message || 'No se pudo eliminar');
    }
  };

  const inputClass =
    'w-full px-5 py-4 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E] bg-stone-50 transition-all';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto space-y-8">
      {/* Header Card */}
      <div className="bg-white rounded-[32px] shadow-xl shadow-stone-200/50 border border-stone-100 overflow-hidden relative">
        <div className="h-32 bg-[#1B2A4E] relative sol-watermark"></div>
        <div className="px-8 pb-8 pt-0 relative">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end -mt-12 gap-4">
            <div className="flex items-end gap-5">
              <img
                src={displayFoto}
                alt=""
                className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg bg-white"
              />
              <div className="mb-2">
                <h2 className="text-3xl font-editorial font-bold text-[#1B2A4E] flex items-center gap-3">
                  {currentUser.nombre}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="label-technical text-[#74ACDF]">{currentUser.rubro}</span>
                  {capituloNombre && (
                    <span className="label-technical text-stone-400 flex items-center gap-1">
                      <SafeIcon name="MapPin" className="w-3 h-3" /> {capituloNombre}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={logout}
              className="mb-2 bg-stone-100 hover:bg-red-50 text-stone-500 hover:text-red-600 px-4 py-2 rounded-xl transition-colors font-bold text-sm flex items-center gap-2"
            >
              <SafeIcon name="LogOut" className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
        <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-[#FAF9F6]">
          <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E]">Información del Perfil</h3>
          <button
            type="button"
            onClick={() => (isEditing ? handleCancelEdit() : setIsEditing(true))}
            className="text-[#74ACDF] text-sm font-bold label-technical hover:text-[#1B2A4E] transition-colors"
            disabled={isSaving}
          >
            {isEditing ? 'Cancelar' : 'Editar Perfil'}
          </button>
        </div>

        <form onSubmit={handleSaveProfile} className="p-8 space-y-8" noValidate>
          {/* Nombre + Avatar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block label-technical text-stone-400 mb-2">Nombre</label>
              {isEditing ? (
                <input
                  type="text"
                  required
                  className={inputClass}
                  value={form.nombre}
                  onChange={(e) => setField('nombre', e.target.value)}
                  disabled={isSaving}
                />
              ) : (
                <p className="text-[#1B2A4E] font-medium text-lg">{currentUser.nombre}</p>
              )}
            </div>

            <div>
              <label className="block label-technical text-stone-400 mb-2">Foto de perfil</label>
              {isEditing ? (
                <div className="flex items-center gap-4">
                  <img
                    src={
                      avatarPreview ||
                      avatarPublicUrl(currentUser.foto_url) ||
                      `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(form.nombre || '?')}`
                    }
                    alt=""
                    className="w-16 h-16 rounded-xl object-cover border border-stone-200 bg-white shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="block w-full text-sm text-stone-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-stone-100 file:text-[#1B2A4E] file:font-bold file:text-sm hover:file:bg-stone-200"
                      onChange={handleAvatarChange}
                      disabled={isSaving}
                    />
                    <p className="text-xs text-stone-400 mt-1">JPG, PNG o WebP · máx. 5 MB</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-stone-500">
                  {currentUser.foto_url ? 'Foto cargada' : 'Sin foto'}
                </p>
              )}
            </div>
          </div>

          {/* Rubro + Capítulo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block label-technical text-stone-400 mb-2">Rubro</label>
              {isEditing ? (
                <select
                  className={inputClass}
                  value={form.rubro}
                  onChange={(e) => setField('rubro', e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">Seleccioná un rubro</option>
                  {RUBROS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                  {form.rubro && !RUBROS.includes(form.rubro) && (
                    <option value={form.rubro}>{form.rubro}</option>
                  )}
                </select>
              ) : (
                <p className="text-[#1B2A4E]">{currentUser.rubro || '—'}</p>
              )}
            </div>
            <div>
              <label className="block label-technical text-stone-400 mb-2">Ciudad / Capítulo</label>
              {isEditing ? (
                <select
                  className={inputClass}
                  value={form.capitulo_id}
                  onChange={(e) => setField('capitulo_id', e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">Seleccioná tu base</option>
                  {(CAPITULOS || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                  {form.capitulo_id &&
                    !(CAPITULOS || []).some((c) => c.id === form.capitulo_id) && (
                      <option value={form.capitulo_id}>Capítulo actual</option>
                    )}
                </select>
              ) : (
                <p className="text-[#1B2A4E]">{capituloNombre || '—'}</p>
              )}
            </div>
          </div>

          {/* WhatsApp */}
          <div className="bg-stone-50 p-6 rounded-3xl border border-stone-100 space-y-4">
            <div>
              <label className="block label-technical text-stone-400 mb-2">
                WhatsApp (con código de país)
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  className={inputClass + ' bg-white'}
                  placeholder="+1 305 555 0101"
                  value={form.whatsapp_number}
                  onChange={(e) => setField('whatsapp_number', e.target.value)}
                  disabled={isSaving}
                />
              ) : (
                <p className="text-[#1B2A4E]">
                  {currentUser.whatsapp_number || '—'}
                  {currentUser.whatsapp_number && (
                    <span className="ml-2 text-xs text-stone-400">
                      {currentUser.whatsapp_visible ? '(visible en directorio)' : '(oculto)'}
                    </span>
                  )}
                </p>
              )}
            </div>
            {isEditing && (
              <label className="flex items-start gap-4 cursor-pointer group">
                <div className="relative flex items-center justify-center mt-1">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={form.whatsapp_visible}
                    onChange={(e) => setField('whatsapp_visible', e.target.checked)}
                    disabled={isSaving}
                  />
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                      form.whatsapp_visible
                        ? 'bg-[#25D366] border-[#25D366]'
                        : 'border-stone-300 bg-white'
                    }`}
                  >
                    {form.whatsapp_visible && <SafeIcon name="Check" className="w-4 h-4 text-white" />}
                  </div>
                </div>
                <div>
                  <span className="block font-bold text-[#1B2A4E] mb-1 group-hover:text-[#74ACDF] transition-colors">
                    Mostrar mi WhatsApp en el directorio
                  </span>
                  <span className="block text-sm text-stone-500 leading-relaxed">
                    Si está activo, otros miembros ven el botón de contacto directo en tu tarjeta. Si
                    no, tu número queda privado.
                  </span>
                </div>
              </label>
            )}
          </div>

          {/* Bio */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block label-technical text-stone-400">Bio corta</label>
              {isEditing && (
                <span
                  className={`text-xs font-medium ${
                    form.bio.length > BIO_MAX_LEN ? 'text-red-500' : 'text-stone-400'
                  }`}
                >
                  {form.bio.length}/{BIO_MAX_LEN}
                </span>
              )}
            </div>
            {isEditing ? (
              <textarea
                className={inputClass}
                rows={3}
                maxLength={BIO_MAX_LEN + 20}
                placeholder="Una línea sobre vos y tu negocio…"
                value={form.bio}
                onChange={(e) => setField('bio', e.target.value)}
                disabled={isSaving}
              />
            ) : (
              <p className="text-stone-600 leading-relaxed">{currentUser.bio || '—'}</p>
            )}
          </div>

          {/* Ofrezco / Busco */}
          <div>
            <label className="block label-technical text-stone-400 mb-2">Lo que ofrezco al Movimiento</label>
            {isEditing ? (
              <textarea
                className={`${inputClass} font-medium italic`}
                rows={3}
                value={form.ofrezco}
                onChange={(e) => setField('ofrezco', e.target.value)}
                disabled={isSaving}
              />
            ) : (
              <p className="text-[#1B2A4E] bg-[#74ACDF]/5 p-6 rounded-2xl border border-[#74ACDF]/10 font-medium italic text-lg leading-relaxed">
                &ldquo;{currentUser.ofrezco}&rdquo;
              </p>
            )}
          </div>
          <div>
            <label className="block label-technical text-stone-400 mb-2">Lo que busco</label>
            {isEditing ? (
              <textarea
                className={inputClass}
                rows={3}
                value={form.busco}
                onChange={(e) => setField('busco', e.target.value)}
                disabled={isSaving}
              />
            ) : (
              <p className="text-stone-600 bg-stone-50 p-6 rounded-2xl border border-stone-100 leading-relaxed">
                {currentUser.busco}
              </p>
            )}
          </div>

          {/* Links */}
          <div className="space-y-4">
            <label className="block label-technical text-stone-400">Links (opcionales)</label>
            {isEditing ? (
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs text-stone-400 mb-1">Instagram</label>
                  <input
                    type="url"
                    className={inputClass}
                    placeholder="https://instagram.com/tuusuario"
                    value={form.instagram_url}
                    onChange={(e) => setField('instagram_url', e.target.value)}
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-xs text-stone-400 mb-1">LinkedIn</label>
                  <input
                    type="url"
                    className={inputClass}
                    placeholder="https://linkedin.com/in/tuusuario"
                    value={form.linkedin_url}
                    onChange={(e) => setField('linkedin_url', e.target.value)}
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-xs text-stone-400 mb-1">Web</label>
                  <input
                    type="url"
                    className={inputClass}
                    placeholder="https://tusitio.com"
                    value={form.web_url}
                    onChange={(e) => setField('web_url', e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 text-sm">
                {currentUser.instagram_url && (
                  <a
                    href={currentUser.instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#74ACDF] font-bold hover:text-[#1B2A4E]"
                  >
                    Instagram
                  </a>
                )}
                {currentUser.linkedin_url && (
                  <a
                    href={currentUser.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#74ACDF] font-bold hover:text-[#1B2A4E]"
                  >
                    LinkedIn
                  </a>
                )}
                {currentUser.web_url && (
                  <a
                    href={currentUser.web_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#74ACDF] font-bold hover:text-[#1B2A4E]"
                  >
                    Web
                  </a>
                )}
                {!currentUser.instagram_url && !currentUser.linkedin_url && !currentUser.web_url && (
                  <span className="text-stone-400">Sin links</span>
                )}
              </div>
            )}
          </div>

          {saveError && isEditing && (
            <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{saveError}</div>
          )}
          {photoError && (
            <div className="p-3 rounded-xl bg-amber-50 text-amber-800 text-sm font-medium">
              Foto: {photoError}
              {!isEditing && ' (el resto del perfil sí se guardó)'}
            </div>
          )}

          {isEditing && (
            <button
              type="submit"
              disabled={isSaving}
              className="w-full bg-[#1B2A4E] text-white font-bold py-4 rounded-2xl hover:bg-[#253969] transition-colors shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
                  Guardando…
                </>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          )}
        </form>
      </div>

      {/* Mis Negocios y Servicios */}
      <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
        <div className="p-8 border-b border-stone-100 bg-[#FAF9F6] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E]">Mis Negocios y Servicios</h3>
            <p className="text-sm text-stone-500 mt-1">
              Perfiles públicos para promocionar lo que ofrecés. Requieren aprobación.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateNegocio}
            className="bg-[#1B2A4E] text-white px-5 py-3 rounded-xl text-sm font-bold hover:bg-[#253969] transition-colors flex items-center justify-center gap-2 shrink-0"
          >
            <SafeIcon name="Plus" className="w-4 h-4" /> Agregar Negocio o Servicio
          </button>
        </div>

        <div className="p-8">
          {negocioError && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{negocioError}</div>
          )}
          {myNegocios.length === 0 ? (
            <div className="text-center py-8 text-stone-400 font-editorial italic">
              Todavía no cargaste ningún negocio o servicio.
            </div>
          ) : (
            <div className="space-y-4">
              {myNegocios.map((n) => (
                <div
                  key={n.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl border border-stone-100 bg-white hover:border-stone-200 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {n.logo_url ? (
                      <img
                        src={negocioFotoPublicUrl(n.logo_url)}
                        alt=""
                        className="w-14 h-14 rounded-xl object-cover border border-stone-200 shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-stone-100 flex items-center justify-center text-stone-400 shrink-0">
                        <SafeIcon name="Briefcase" className="w-6 h-6" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="font-bold text-[#1B2A4E] truncate">{n.nombre}</h4>
                        <span className="px-2 py-0.5 rounded-full label-technical text-[10px] bg-[#1B2A4E]/5 text-[#1B2A4E]">
                          {negocioTipoToUi(n.tipo)}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full label-technical text-[10px] border ${STATUS_BADGE_CLASS[n.status] || STATUS_BADGE_CLASS.pending}`}
                        >
                          {negocioStatusToUi(n.status)}
                        </span>
                      </div>
                      {n.categoria && <p className="text-sm text-stone-500 truncate">{n.categoria}</p>}
                      {(n.status === 'approved' || n.status === 'pending') && (
                        <Link
                          to={`/negocio/${n.slug}`}
                          className="text-xs font-bold text-[#74ACDF] hover:text-[#1B2A4E] mt-1 inline-flex items-center gap-1"
                        >
                          {n.status === 'approved' ? 'Ver página pública' : 'Vista previa'}{' '}
                          <SafeIcon name="ExternalLink" className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => openEditNegocio(n)}
                      className="px-4 py-2 rounded-xl text-sm font-bold bg-stone-100 text-[#1B2A4E] hover:bg-stone-200 transition-colors flex items-center gap-1.5"
                    >
                      <SafeIcon name="Edit3" className="w-4 h-4" /> Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteNegocio(n)}
                      className="px-4 py-2 rounded-xl text-sm font-bold bg-white border border-red-200 text-red-500 hover:bg-red-50 transition-colors flex items-center gap-1.5"
                    >
                      <SafeIcon name="Trash2" className="w-4 h-4" /> Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Badges Management Card */}
      <div className="bg-white rounded-[32px] shadow-sm border border-stone-100 overflow-hidden">
        <div className="p-8 border-b border-stone-100 bg-[#FAF9F6] flex items-center justify-between">
          <div>
            <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E]">Mis Insignias</h3>
            <p className="text-sm text-stone-500 mt-1">
              Elegí hasta 3 insignias para destacar en tu tarjeta del directorio.
            </p>
          </div>
          <Link
            to="/insignias"
            className="text-stone-400 hover:text-[#74ACDF] transition-colors"
            title="Info de insignias"
          >
            <SafeIcon name="Info" className="w-5 h-5" />
          </Link>
        </div>

        <div className="p-8">
          {userBadges.length === 0 ? (
            <div className="text-center py-8 text-stone-400 font-editorial italic">
              Aún no has obtenido insignias. ¡Participá en la comunidad para ganar reconocimientos!
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <span className="label-technical text-stone-400">Insignias Obtenidas</span>
                <span className="label-technical text-[#74ACDF]">{featuredCount}/3 Destacadas</span>
              </div>

              {userBadges.map((b) => {
                const info = BADGES_INFO[b.id];
                if (!info) return null;
                const isAbanderado = b.id === 'abanderado';
                const isDisabled = isAbanderado || (!b.featured && featuredCount >= 3);

                return (
                  <div
                    key={b.id}
                    className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${b.featured ? 'border-[#74ACDF] bg-[#74ACDF]/5' : 'border-stone-100 bg-white'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${isAbanderado ? 'bg-white border border-stone-200 shadow-sm' : 'bg-white text-stone-500 border border-stone-200'}`}
                      >
                        {isAbanderado ? (
                          <EscarapelaIcon className="w-7 h-7" />
                        ) : (
                          <SafeIcon name={info.icon} className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-bold text-[#1B2A4E] text-sm leading-tight">{info.name}</h4>
                        <p className="text-xs text-stone-500 mt-1 max-w-sm">{info.description}</p>
                      </div>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer pl-4">
                      <span className="text-xs font-bold text-stone-400 hidden sm:block">
                        {isAbanderado ? 'Siempre Destacada' : b.featured ? 'Destacada' : 'Destacar'}
                      </span>
                      <div className="relative flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={b.featured}
                          disabled={isDisabled}
                          onChange={() => handleToggleFeatured(b.id)}
                        />
                        <div
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${b.featured ? 'bg-[#74ACDF] border-[#74ACDF]' : 'border-stone-300 bg-white'} ${isDisabled && !isAbanderado ? 'opacity-50' : ''}`}
                        >
                          {b.featured && <SafeIcon name="Check" className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <SolDivider />

      {/* Premium Referral Card */}
      <div className="bg-gradient-to-br from-[#74ACDF] to-[#1B2A4E] rounded-[40px] p-10 md:p-12 text-white relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full -mr-40 -mt-40 blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#F6B40E] rounded-full flex items-center justify-center text-[#1B2A4E]">
              <SafeIcon name="Link" className="w-5 h-5" />
            </div>
            <h3 className="text-3xl font-editorial font-bold">Tu Enlace de Invitación</h3>
          </div>
          <p className="text-white/80 text-lg mb-8 max-w-lg">
            El Movimiento crece por recomendaciones de calidad. Invitá a otros emprendedores que sumen
            valor a la red.
          </p>
          <div className="flex flex-col sm:flex-row p-1.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
            <input
              readOnly
              value={`movimiento.com/invitacion/${currentUser.invite_code}`}
              className="bg-transparent border-none text-white px-5 py-3 flex-1 text-sm outline-none font-medium"
            />
            <button
              type="button"
              className="bg-[#F6B40E] text-[#1B2A4E] px-8 py-3 rounded-xl font-bold text-sm hover:bg-white transition-colors mt-2 sm:mt-0"
            >
              Copiar Link
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {(showSaveSuccess || negocioSuccess) && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 50, x: '-50%' }}
            className="fixed bottom-24 left-1/2 z-50 bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl font-medium flex items-center gap-3"
          >
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center shrink-0">
              <SafeIcon name="Check" className="w-5 h-5" />
            </div>
            {negocioSuccess || 'Perfil actualizado'}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {negocioModalOpen && (
          <NegocioFormModal
            open={negocioModalOpen}
            listing={editingNegocio}
            onClose={() => setNegocioModalOpen(false)}
            onSubmit={handleNegocioSubmit}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
